import { GetStaticPropsResult, GetStaticPathsResult } from 'next'
import { ParsedUrlQuery } from 'querystring'

import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import {
  SanityProductCatalogueQuery,
  SanityProductPage,
} from '@data/sanity/queries/types/product'
import { getShopifyDomain } from '@lib/shopify/client'
import { getPreview } from '@lib/sanity/client'
import { getAllProductSlugs } from '@lib/sanity/page'
import { getProductCatalogue, getProductPage } from '@lib/sanity/product'
import { getAllLocales, Locale } from '@lib/language'
import { getReviews, Reviews } from '@lib/review'
import { CustomStaticPropsContext, StaticPathWithSlug } from '@lib/routes'
import {
  useProductWithInventory,
  useActiveVariant,
  useDigitalProductContent,
} from '@lib/product'
import { useVariantMetadata } from '@lib/metadata'
import { useProductSchema } from '@lib/schema'
import { useViewContentFacebookEvent } from '@lib/facebook'

import Modules from '@modules/modules'
import Layout from '@components/layout'

interface ProductPageProps {
  locale: Locale
  page: SanityProductPage
  site: SanitySiteFragment
  productCatalogue: SanityProductCatalogueQuery
  reviews?: Reviews
}

interface StaticProductPageParams extends ParsedUrlQuery {
  slug: string
}

/**
 * The product page.
 */
const ProductPage = ({ locale, page, site, reviews }: ProductPageProps) => {
  // Get product and active variant
  const product = useProductWithInventory(page, locale)
  const [activeVariant, updateProductPageUrl] = useActiveVariant(product)

  // Get product schema
  const productSchema = useProductSchema(site, product, activeVariant)

  // Load the correct digital product content
  const [showPrivateContent, isLoaded] = useDigitalProductContent(page.product)

  // Trigger Facebook event
  useViewContentFacebookEvent(
    locale,
    isLoaded,
    product.title,
    activeVariant,
    site.settings.facebookEvents
  )

  // Set product variant metadata
  useVariantMetadata(activeVariant)

  const pageModules = showPrivateContent ? page.privateModules : page.modules

  if (!isLoaded || !pageModules) {
    return null
  }

  return (
    <Layout page={page} site={site} productSchema={productSchema}>
      <Modules
        modules={page.modules}
        product={product}
        activeVariant={activeVariant}
        onVariantChange={updateProductPageUrl}
        reviews={reviews}
      />
    </Layout>
  )
}

export async function getStaticProps({
  locale,
  params,
  preview,
  previewData,
}: CustomStaticPropsContext<StaticProductPageParams>): Promise<
  GetStaticPropsResult<ProductPageProps>
> {
  const productPage = await getProductPage(
    locale,
    params?.slug ?? '',
    getPreview(preview, previewData)
  )

  if (!productPage.page) {
    return { notFound: true }
  }

  const productCatalogue = await getProductCatalogue(locale)

  // Get Stamped.io reviews
  const shopifyDomain = getShopifyDomain(locale)
  const reviews = await getReviews(
    productPage.page.modules ?? [],
    shopifyDomain,
    productPage.site.settings.stampedApiKey,
    productPage.page.product.id
  )

  return {
    props: {
      locale,
      page: productPage.page,
      site: productPage.site,
      productCatalogue,
      reviews,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticProductPageParams>
> {
  const allProducts = await getAllProductSlugs()
  const allLocales = getAllLocales()
  const paths: StaticPathWithSlug[] = allProducts
    .map((product) => ({
      ...product,
      locale: product.locale as Locale,
    }))
    .filter(({ locale }) => allLocales.includes(locale))
    .filter(({ slug }) => !!slug)
    .map(({ locale, slug }) => ({
      params: {
        slug,
      },
      locale,
    }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export default ProductPage
